import authClientBase from '@exodus/auth-client-base'
import urlJoin from 'url-join'
import { buildQueryParamsWithProviders } from './utils.js'

const { createClient } = authClientBase

const fiatClient = ({ fetch, keyPair, metadata, url } = {}) => {
  const baseUrl = urlJoin(url, 'api')
  const authChallengeUrl = urlJoin(baseUrl, 'auth/challenge')
  const authTokenUrl = urlJoin(baseUrl, 'auth/token')
  const client = createClient({
    fetch,
    config: { authChallengeUrl, authTokenUrl, baseUrl, keyPair },
    metadata,
  })

  const createOrder = async (parameters = {}) =>
    client.post('/order', { auth: true, data: parameters })

  const updateOrder = async (id, { provider, ...parameters } = {}) => {
    const encodedId = encodeURIComponent(id)
    return client.post(`/v1/orders/${encodedId}/webhook?${new URLSearchParams({ provider })}`, {
      auth: true,
      data: parameters,
    })
  }

  const updateUser = async (parameters = Object.create(null)) =>
    client.put('/v1/me', { auth: true, data: parameters })

  const addOrderEvent = async (parameters = {}) =>
    client.post('/order-events', { auth: true, data: parameters })

  const createOrderDeposit = async (id) => {
    const encodedId = encodeURIComponent(id)
    await client.post(`/order/${encodedId}/deposit`, { auth: true })
  }

  const getAssets = async (parameters) =>
    client.get('/v1/assets', { auth: true, query: buildQueryParamsWithProviders(parameters) })

  const getCountries = async (parameters) =>
    client.get('/v1/countries', { auth: true, query: buildQueryParamsWithProviders(parameters) })

  const getOrder = async (id) => client.get('/order', { auth: true, query: { orderId: id } })

  const getOrders = async ({ provider, orderId } = {}) => {
    if (provider) {
      const encodedProvider = encodeURIComponent(provider)
      const encodedOrderId = encodeURIComponent(orderId)

      return client.get(
        orderId ? `/orders/${encodedProvider}/${encodedOrderId}` : `/orders/${encodedProvider}`,
        {
          auth: true,
        }
      )
    }

    return client.get('/orders', { auth: true })
  }

  const getQuotes = async (parameters) =>
    client.get('/v1/quotes', { auth: true, query: buildQueryParamsWithProviders(parameters) })

  const getUser = async () => {
    const [{ value: legacyUser }, { value: user }] = await Promise.allSettled([
      client.get('/has-user-passed-kyc', { auth: true }),
      client.get('/v1/me', { auth: true }),
    ])

    return { kyc: legacyUser?.value, ...user }
  }

  const getProviderToken = async ({ provider, ...parameters }) => {
    const encodedProvider = encodeURIComponent(provider)
    return client.post(`/auth/${encodedProvider}`, { auth: true, data: parameters })
  }

  const getToken = async () => {
    await client._authenticate()
    return client.token
  }

  const sign = async ({ data, provider }) =>
    client.post('/sign', { auth: true, data: { data, provider } })

  const signUrl = async ({ provider, url } = {}) =>
    client.get('/sign-url', { auth: false, query: { provider, url } })

  const hasUserPassedKYC = async () => {
    const { kyc } = await getUser()
    return { success: true, value: kyc }
  }

  return {
    ...client,
    createOrder,
    createOrderDeposit,
    addOrderEvent,
    getAssets,
    getCountries,
    getOrder,
    getOrders,
    getProviderToken,
    getQuotes,
    getToken,
    getUser,
    sign,
    signUrl,
    hasUserPassedKYC,
    updateOrder,
    updateUser,
  }
}

export default fiatClient
